import { useMemo, useState } from 'react';
import { Activity, Filter } from 'lucide-react';
import { trpc } from '../lib/trpc.ts';
import { Badge } from '../components/Badge.tsx';
import { EmptyState } from '../components/EmptyState.tsx';
import { useLiveStream } from '../components/LiveStream.tsx';
import { relativeTime, timeOfDay } from '../lib/format.ts';
import { eventLabels, type EventKind } from '@cdvi-edge/shared';

type Severity = 'info' | 'warn' | 'critical';

export function Events() {
  const [severities, setSeverities] = useState<Severity[]>(['info', 'warn', 'critical']);
  const list = trpc.event.list.useQuery({ limit: 100, severities });
  const live = useLiveStream();

  const merged = useMemo(() => {
    const seen = new Set<string>();
    const out: Array<{
      id?: string;
      occurredAt: string;
      kind: string;
      severity: Severity;
      payload?: unknown;
    }> = [];
    for (const e of live.events) {
      if (!severities.includes(e.severity as Severity)) continue;
      const k = `${e.occurredAt}-${e.kind}`;
      if (seen.has(k)) continue;
      seen.add(k);
      out.push(e as unknown as (typeof out)[number]);
    }
    for (const e of list.data ?? []) {
      const k = `${e.occurredAt}-${e.kind}`;
      if (seen.has(k)) continue;
      seen.add(k);
      out.push(e as unknown as (typeof out)[number]);
    }
    return out.slice(0, 200);
  }, [list.data, live.events, severities]);

  function toggle(s: Severity) {
    setSeverities((cur) => (cur.includes(s) ? cur.filter((x) => x !== s) : [...cur, s]));
  }

  return (
    <div className="stack">
      <div className="card tight">
        <div className="row">
          <Filter size={14} className="muted" />
          <span className="dim">Severity</span>
          {(['info', 'warn', 'critical'] as Severity[]).map((s) => (
            <button
              key={s}
              className={severities.includes(s) ? '' : 'secondary'}
              onClick={() => toggle(s)}
              style={{ padding: '4px 10px', fontSize: 12 }}
            >
              {s}
            </button>
          ))}
          <span className="spacer" />
          {live.connected ? <Badge tone="ok">live</Badge> : <Badge tone="warn">offline</Badge>}
        </div>
      </div>

      <div className="card">
        {merged.length === 0 ? (
          <EmptyState
            icon={Activity}
            title="No events match"
            body="Try widening the severity filter or wait for activity."
          />
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>Time</th>
                <th>Severity</th>
                <th>Event</th>
                <th>Payload</th>
              </tr>
            </thead>
            <tbody>
              {merged.map((e, i) => (
                <tr key={`${e.occurredAt}-${e.kind}-${i}`}>
                  <td className="mono dim" title={e.occurredAt}>
                    {timeOfDay(e.occurredAt)}
                    <div className="dim" style={{ fontSize: 11 }}>{relativeTime(e.occurredAt)}</div>
                  </td>
                  <td>
                    <Badge
                      tone={
                        e.severity === 'critical' ? 'danger' : e.severity === 'warn' ? 'warn' : 'info'
                      }
                    >
                      {e.severity}
                    </Badge>
                  </td>
                  <td>{eventLabels[e.kind as EventKind] ?? e.kind}</td>
                  <td className="dim mono" style={{ fontSize: 11, wordBreak: 'break-word' }}>
                    {e.payload ? JSON.stringify(e.payload) : ''}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
